import React from 'react'
import { Box, Tab, Tabs } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import { poolwerxColors } from '../../../theme/colors'

export interface AppDrawerTabDef<V extends string = string> {
  value: V
  label: React.ReactNode
  icon?: React.ReactElement
  /**
   * Optional count rendered as a pill badge after the label. Omit entirely
   * (``undefined``) to render no badge (e.g. the feedback drawer's tabs, or a
   * tab whose count isn't known yet). A count of ``0`` IS rendered - as a muted
   * grey badge - so a caller that always wants a badge (e.g. the exception
   * drawer's Issues/Activity/Notes) can show "0" rather than hiding it.
   */
  count?: number
  /**
   * Strongly accent the count badge (filled teal + white text) even when the
   * tab is inactive, so a non-zero count reads as "there's human context here"
   * at a glance (e.g. the exception drawer's Notes tab). Zero still reads muted.
   */
  emphasizeCount?: boolean
}

export interface AppDrawerTabsProps<V extends string = string> {
  tabs: AppDrawerTabDef<V>[]
  value: V
  onChange: (next: V) => void
  variant?: 'fullWidth' | 'standard' | 'scrollable'
  sx?: SxProps<Theme>
}

// Pill count badge after a tab label. Three tiers, all existing tokens:
//  - ``emphasize`` + non-zero -> FILLED teal + white text. The loudest signal,
//    reserved for the Notes tab so "there's human context here" reads even when
//    the tab is inactive.
//  - active + non-zero        -> pale teal fill + teal text (the selected tab's
//    own count).
//  - zero (any state)         -> muted grey. An empty tab shouldn't draw the eye.
const countBadgeSx = (
  active: boolean,
  zero: boolean,
  emphasize: boolean,
): SxProps<Theme> => {
  const strong = emphasize && !zero
  const accent = active && !zero
  return {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '11px',
    lineHeight: 1.4,
    fontWeight: strong || accent ? 600 : 500,
    borderRadius: '20px',
    px: '8px',
    py: '1px',
    bgcolor: strong
      ? poolwerxColors.teal
      : accent
        ? poolwerxColors.tealLight
        : poolwerxColors.surface.secondary,
    color: strong
      ? '#fff'
      : accent
        ? poolwerxColors.teal
        : poolwerxColors.text.tertiary,
  }
}

function AppDrawerTabs<V extends string = string>({
  tabs,
  value,
  onChange,
  variant = 'standard',
  sx,
}: AppDrawerTabsProps<V>) {
  return (
    <Tabs
      value={value}
      onChange={(_e, next) => onChange(next as V)}
      variant={variant}
      sx={{
        minHeight: 'auto',
        px: '22px',
        bgcolor: 'background.paper',
        borderBottom: `0.5px solid ${poolwerxColors.border.tertiary}`,
        // Active tab + 2px underline are teal by default, so every app drawer
        // (exception + feedback) shares one tab treatment.
        '& .MuiTabs-indicator': {
          backgroundColor: poolwerxColors.teal,
          height: 2,
        },
        '& .MuiTabs-flexContainer': { gap: '28px' },
        '& .MuiTab-root': {
          minHeight: 'auto',
          minWidth: 'auto',
          p: 0,
          py: '11px',
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '14px',
          color: poolwerxColors.text.secondary,
          '&.Mui-selected': {
            color: poolwerxColors.teal,
            fontWeight: 600,
          },
        },
        ...sx,
      }}
    >
      {tabs.map((t) => {
        const active = t.value === value
        const label =
          t.count === undefined ? (
            t.label
          ) : (
            <Box
              component="span"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}
            >
              {t.label}
              <Box
                component="span"
                sx={countBadgeSx(active, t.count === 0, Boolean(t.emphasizeCount))}
              >
                {t.count}
              </Box>
            </Box>
          )
        return (
          <Tab
            key={t.value}
            value={t.value}
            label={label}
            icon={t.icon}
            iconPosition={t.icon ? 'start' : undefined}
          />
        )
      })}
    </Tabs>
  )
}

export default AppDrawerTabs
